// Decisions inbox — "What needs your decision".
//
// Direct port of the design's `DecisionsInbox` (loom/project/dashboard.jsx).
// Reads `Decision[]` straight off the dashboard contract; the kind drives
// the rail colour + tag, the action string drives the button label. The
// row + the action button share one handler; the inner button stops
// propagation so the row click doesn't double-fire.

import type { JSX } from "react";

import type { Decision, DecisionKind } from "@loom/shared/dashboard";

import { useFieldCopy } from "../../focus/copy";
import { useT, type MessageKey } from "../../i18n/index.js";

import { Avatar } from "./Avatar";
import { Icon } from "./Icon";
import { Spinner } from "./Spinner";

export type DecisionActionVerb = "add_detail" | "answer" | "review" | "split_it";

interface KindMeta {
  readonly tagKey: MessageKey;
  readonly cls: string;
}

const KIND_META: Record<DecisionKind, KindMeta> = {
  clarify: { tagKey: "dashboard.inbox.tag.clarify", cls: "k-clarify" },
  proposal: { tagKey: "dashboard.inbox.tag.proposal", cls: "k-proposal" },
  blocked: { tagKey: "dashboard.inbox.tag.blocked", cls: "k-blocked" },
};

/**
 * Decision rows carry a free-form `action` label ("Add detail", "Answer",
 * "Review", "Split it"); the dashboard needs the verb form to route to
 * editor / chat-drawer / decompose stubs. Map on the label so the
 * server stays the single source of truth for what's shown.
 */
const verbFor = (decision: Decision): DecisionActionVerb => {
  switch (decision.action) {
    case "Add detail":
      return "add_detail";
    case "Split it":
      return "split_it";
    case "Review":
      return "review";
    case "Answer":
    default:
      return "answer";
  }
};

interface DecisionsInboxProps {
  readonly decisions: ReadonlyArray<Decision> | null;
  readonly loading?: boolean;
  readonly onDecisionAction: (
    decision: Decision,
    action: DecisionActionVerb,
  ) => void;
}

export const DecisionsInbox = ({
  decisions,
  loading,
  onDecisionAction,
}: DecisionsInboxProps): JSX.Element => {
  const t = useT();
  // `missing` carries spec FIELD IDS. Rendered raw they read as "role",
  // "ends", "acceptance_criteria" — internal names, in English, in a German
  // interface. This is the same label catalogue the readiness card's own DoR
  // line uses.
  const fieldCopy = useFieldCopy();
  if (decisions === null) {
    return (
      <section className="inbox">
        <div className="inbox-head">
          <div className="inbox-head-left">
            <span className="inbox-eyebrow">
              <Icon name="inbox" size={12} /> {t("dashboard.inbox.forYou")}
            </span>
            <h2 className="inbox-title">{t("dashboard.inbox.title")}</h2>
          </div>
        </div>
        <div className="inbox-skeleton">
          <Spinner size={18} label={loading ? t("dashboard.inbox.loadingDecisions") : ""} />
        </div>
      </section>
    );
  }
  if (decisions.length === 0) {
    return (
      <section className="inbox">
        <div className="inbox-head">
          <div className="inbox-head-left">
            <span className="inbox-eyebrow">
              <Icon name="inbox" size={12} /> {t("dashboard.inbox.forYou")}
            </span>
            <h2 className="inbox-title">{t("dashboard.inbox.title")}</h2>
          </div>
          <span className="inbox-count">{t("dashboard.inbox.countOpen", { count: 0 })}</span>
        </div>
        <div className="inbox-empty">{t("dashboard.inbox.empty")}</div>
      </section>
    );
  }

  return (
    <section className="inbox">
      <div className="inbox-head">
        <div className="inbox-head-left">
          <span className="inbox-eyebrow">
            <Icon name="inbox" size={12} /> {t("dashboard.inbox.forYou")}
          </span>
          <h2 className="inbox-title">{t("dashboard.inbox.title")}</h2>
        </div>
        <span className="inbox-count">{t("dashboard.inbox.countOpen", { count: decisions.length })}</span>
      </div>
      <div className="inbox-list">
        {decisions.map((d) => {
          const meta = KIND_META[d.kind];
          const verb = verbFor(d);
          const fire = (): void => onDecisionAction(d, verb);
          return (
            <div
              key={d.id}
              className={`inbox-row ${meta.cls}`}
              onClick={fire}
            >
              <div className="inbox-rail" />
              <div className="inbox-main">
                <div className="inbox-row-head">
                  <span className="inbox-tag">{t(meta.tagKey)}</span>
                  <span className="inbox-ref">{d.ref}</span>
                  <span className="inbox-age">{d.age}</span>
                </div>
                <div className="inbox-row-title">{d.title}</div>
                {d.body.length > 0 && (
                  <div className="inbox-row-body">{d.body}</div>
                )}
                {d.missing && d.missing.length > 0 && (
                  <div className="inbox-missing">
                    {d.missing.map((m, i) => (
                      <span key={i} className="missing-chip">
                        <Icon name="flag" size={10} />
                        {fieldCopy(m).label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="inbox-action">
                {d.who && <Avatar handle={d.who} size={22} />}
                <button
                  className="inbox-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    fire();
                  }}
                >
                  {d.action}
                  {d.count ? (
                    <span className="inbox-btn-count">{d.count}</span>
                  ) : null}
                  <Icon name="chevron-right" size={13} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
